import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import {
  downloadRseExcel,
  getRseCalidad,
  getRseComunidades,
  getRseEdadSexo,
  getRsePatologias,
  getRsePatologiasComunidad,
  getRseProjects,
  getRseSummary,
} from "./reporteSaludEmergenciaApi";
import {
  RSE_INITIAL_FILTERS,
  RSE_MONTH_OPTIONS,
  RSE_PERIOD_OPTIONS,
  buildRseQueryParams,
  canAccessRse,
  canExportRse,
  changeRsePeriodMode,
  formatRseNumber,
  getDownloadFilename,
  getRseErrorMessage,
  getRseYearOptions,
  getStoredRseUser,
  validateRseFilters,
} from "./reporteSaludEmergenciaUtils";
import EdadSexoChart from "./components/EdadSexoChart";
import TopComunidadesChart from "./components/TopComunidadesChart";
import TopPatologiasChart from "./components/TopPatologiasChart";
import CalidadDatos from "./components/CalidadDatos";
import PatologiasComunidad from "./components/PatologiasComunidad";

const KPI_ITEMS = [
  { key: "atenciones", label: "Atenciones", color: "#009fe3" },
  { key: "personasUnicas", label: "Personas únicas estimadas", color: "#008080" },
  { key: "jornadas", label: "Jornadas", color: "#22a06b" },
  { key: "comunidades", label: "Comunidades", color: "#f39313" },
  { key: "aprobadas", label: "Aprobadas", color: "#d94841" },
  { key: "reconsultasEstimadas", label: "Reconsultas estimadas", color: "#004f7c" },
];

const surfaceSx = {
  width: "100%",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
};

const EMPTY_ANALYTICS = Object.freeze({
  edadSexo: null,
  comunidades: null,
  patologias: null,
  calidad: null,
});

const ANALYTICS_LOADERS = [
  { key: "edadSexo", load: getRseEdadSexo },
  { key: "comunidades", load: getRseComunidades },
  { key: "patologias", load: getRsePatologias },
  { key: "calidad", load: getRseCalidad },
];

export const ReporteSaludEmergenciaRouteGuard = ({ children }) =>
  canAccessRse(getStoredRseUser()) ? children : <Navigate to="/accessDenied" replace />;

const ReporteSaludEmergencia = () => {
  const user = useMemo(() => getStoredRseUser(), []);
  const canDownload = canExportRse(user);
  const yearOptions = useMemo(() => getRseYearOptions(), []);

  const [filters, setFilters] = useState({ ...RSE_INITIAL_FILTERS });
  const [projects, setProjects] = useState([]);
  const [projectScope, setProjectScope] = useState("");
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [projectsError, setProjectsError] = useState("");

  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState("");
  const [analyticsRequested, setAnalyticsRequested] = useState(false);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState({ ...EMPTY_ANALYTICS });
  const [analyticsErrors, setAnalyticsErrors] = useState({ ...EMPTY_ANALYTICS });

  const [isDownloading, setIsDownloading] = useState(false);
  const [exportError, setExportError] = useState("");
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [pathologyCommunityStatus, setPathologyCommunityStatus] = useState("idle");
  const [pathologyCommunityData, setPathologyCommunityData] = useState(null);
  const [pathologyCommunityError, setPathologyCommunityError] = useState("");

  const projectsRequestSequence = useRef(0);
  const summaryRequestSequence = useRef(0);
  const pathologyCommunityRequestSequence = useRef(0);

  const loadProjects = useCallback(async () => {
    const requestId = ++projectsRequestSequence.current;
    setIsLoadingProjects(true);
    setProjectsError("");

    try {
      const result = await getRseProjects();
      if (requestId !== projectsRequestSequence.current) return;

      setProjects(result.projects);
      setProjectScope(result.projectScope);
    } catch (error) {
      if (requestId !== projectsRequestSequence.current) return;
      setProjects([]);
      setProjectsError(getRseErrorMessage(error, "projects"));
    } finally {
      if (requestId === projectsRequestSequence.current) setIsLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();

    return () => {
      projectsRequestSequence.current += 1;
      summaryRequestSequence.current += 1;
      pathologyCommunityRequestSequence.current += 1;
    };
  }, [loadProjects]);

  const allProjectsLabel =
    projectScope === "global" ? "Todos los proyectos" : "Todos mis proyectos";

  const projectOptions = useMemo(
    () => [{ id: null, isAll: true, label: allProjectsLabel }, ...projects],
    [allProjectsLabel, projects]
  );

  const selectedProject = useMemo(
    () =>
      projectOptions.find((option) =>
        filters.projectId === null
          ? option.id === null
          : String(option.id) === String(filters.projectId)
      ) || projectOptions[0],
    [filters.projectId, projectOptions]
  );

  const selectedProjectLabel = useMemo(
    () =>
      selectedProject?.isAll
        ? selectedProject.label
        : [selectedProject?.codigo, selectedProject?.nombre].filter(Boolean).join(" · "),
    [selectedProject]
  );

  const selectedPeriodLabel = useMemo(() => {
    if (filters.periodMode === "year") return filters.year ? `Año ${filters.year}` : "Año sin seleccionar";
    if (filters.periodMode === "month") {
      const month = RSE_MONTH_OPTIONS.find((option) => String(option.value) === String(filters.month));
      return month && filters.year ? `${month.label} ${filters.year}` : "Mes sin seleccionar";
    }
    if (filters.periodMode === "range") {
      return filters.desde && filters.hasta
        ? `${filters.desde} al ${filters.hasta}`
        : "Rango sin completar";
    }
    return "Histórico completo";
  }, [filters.desde, filters.hasta, filters.month, filters.periodMode, filters.year]);

  const clearPathologyCommunity = () => {
    pathologyCommunityRequestSequence.current += 1;
    setPathologyCommunityStatus("idle");
    setPathologyCommunityData(null);
    setPathologyCommunityError("");
  };

  const clearPreviousSummary = () => {
    summaryRequestSequence.current += 1;
    setIsLoadingSummary(false);
    setIsLoadingAnalytics(false);
    setSummary(null);
    setSummaryError("");
    setAnalyticsRequested(false);
    setAnalyticsData({ ...EMPTY_ANALYTICS });
    setAnalyticsErrors({ ...EMPTY_ANALYTICS });
    setExportError("");
    clearPathologyCommunity();
  };

  const updateFilter = (key, value) => {
    clearPreviousSummary();
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const updatePeriodMode = (periodMode) => {
    clearPreviousSummary();
    setFilters((current) => changeRsePeriodMode(current, periodMode));
  };

  const handleSummary = async () => {
    const validation = validateRseFilters(filters);
    if (!validation.valid) {
      setSummaryError(validation.message);
      return;
    }

    const requestId = ++summaryRequestSequence.current;
    const params = buildRseQueryParams(filters);
    setIsLoadingSummary(true);
    setIsLoadingAnalytics(true);
    setSummaryError("");
    setSummary(null);
    setAnalyticsRequested(true);
    setAnalyticsData({ ...EMPTY_ANALYTICS });
    setAnalyticsErrors({ ...EMPTY_ANALYTICS });

    try {
      const result = await getRseSummary(params);
      if (requestId !== summaryRequestSequence.current) return;
      setSummary(result?.totals || {});
    } catch (error) {
      if (requestId !== summaryRequestSequence.current) return;
      setSummary(null);
      setSummaryError(getRseErrorMessage(error, "summary"));
    } finally {
      if (requestId === summaryRequestSequence.current) setIsLoadingSummary(false);
    }

    if (requestId !== summaryRequestSequence.current) return;

    for (const dataset of ANALYTICS_LOADERS) {
      if (requestId !== summaryRequestSequence.current) return;

      try {
        const result = await dataset.load(params);
        if (requestId !== summaryRequestSequence.current) return;
        setAnalyticsData((current) => ({ ...current, [dataset.key]: result }));
      } catch (error) {
        if (requestId !== summaryRequestSequence.current) return;
        setAnalyticsErrors((current) => ({
          ...current,
          [dataset.key]: getRseErrorMessage(error, dataset.key),
        }));
      }
    }

    if (requestId === summaryRequestSequence.current) setIsLoadingAnalytics(false);
  };

  const handleDownload = async () => {
    if (isDownloading) return;

    const validation = validateRseFilters(filters);
    if (!validation.valid) {
      setExportError(validation.message);
      return;
    }

    setIsDownloading(true);
    setExportError("");
    let objectUrl = "";

    try {
      const response = await downloadRseExcel(buildRseQueryParams(filters));
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      const filename = getDownloadFilename(response.headers);
      objectUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setDownloadSuccess(true);
    } catch (error) {
      setExportError(getRseErrorMessage(error, "export"));
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setIsDownloading(false);
    }
  };

  const handlePathologyCommunity = async () => {
    const validation = validateRseFilters(filters);
    if (!validation.valid) {
      setPathologyCommunityStatus("error");
      setPathologyCommunityError(validation.message);
      return;
    }

    const requestId = ++pathologyCommunityRequestSequence.current;
    const params = buildRseQueryParams(filters);
    setPathologyCommunityStatus("loading");
    setPathologyCommunityData(null);
    setPathologyCommunityError("");

    try {
      const result = await getRsePatologiasComunidad(params);
      if (requestId !== pathologyCommunityRequestSequence.current) return;
      setPathologyCommunityData(result || { rows: [] });
      setPathologyCommunityStatus("loaded");
    } catch {
      if (requestId !== pathologyCommunityRequestSequence.current) return;
      setPathologyCommunityData(null);
      setPathologyCommunityStatus("error");
      setPathologyCommunityError(
        "No fue posible cargar el detalle de patologías por comunidad."
      );
    }
  };

  const showHistoricalWarning =
    filters.projectId === null && filters.periodMode === "history";
  const isQuerying = isLoadingSummary || isLoadingAnalytics;

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        width: "100%",
        maxWidth: "none",
        px: { xs: 2, sm: 2, md: 3, lg: 3, xl: 4 },
        py: { xs: 2, md: 3 },
      }}
    >
      <Stack spacing={0.75} mb={3}>
        <Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
          Reporte Salud Emergencia
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Genera reportes consolidados por proyecto y período.
        </Typography>
      </Stack>

      <Paper sx={{ ...surfaceSx, p: { xs: 2, md: 2.5 }, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
          Filtros del reporte
        </Typography>

        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={6} lg={4}>
            <Autocomplete
              fullWidth
              options={projectOptions}
              value={selectedProject}
              loading={isLoadingProjects}
              disabled={isLoadingProjects && projects.length === 0}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              getOptionLabel={(option) =>
                option.isAll
                  ? option.label
                  : [option.codigo, option.nombre].filter(Boolean).join(" · ")
              }
              onChange={(event, option) => updateFilter("projectId", option?.id ?? null)}
              noOptionsText="No hay proyectos disponibles"
              loadingText="Cargando proyectos..."
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Proyecto"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingProjects && (
                          <CircularProgress color="inherit" size={18} aria-label="Cargando proyectos" />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField
              select
              fullWidth
              label="Período"
              value={filters.periodMode}
              onChange={(event) => updatePeriodMode(event.target.value)}
            >
              {RSE_PERIOD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {(filters.periodMode === "year" || filters.periodMode === "month") && (
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <TextField
                select
                fullWidth
                label="Año"
                value={filters.year}
                onChange={(event) => updateFilter("year", event.target.value)}
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={String(year)}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {filters.periodMode === "month" && (
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <TextField
                select
                fullWidth
                label="Mes"
                value={filters.month}
                onChange={(event) => updateFilter("month", event.target.value)}
              >
                {RSE_MONTH_OPTIONS.map((month) => (
                  <MenuItem key={month.value} value={String(month.value)}>
                    {month.value} {month.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {filters.periodMode === "range" && (
            <>
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Desde"
                  value={filters.desde}
                  onChange={(event) => updateFilter("desde", event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Hasta"
                  value={filters.hasta}
                  onChange={(event) => updateFilter("hasta", event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} lg>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              justifyContent={{ lg: "flex-end" }}
            >
              <Button
                variant="contained"
                startIcon={
                  isQuerying ? (
                    <CircularProgress size={18} color="inherit" aria-label="Consultando resumen" />
                  ) : (
                    <AssessmentOutlinedIcon />
                  )
                }
                onClick={handleSummary}
                disabled={isQuerying}
                sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 190 } }}
              >
                {isQuerying ? "Consultando..." : "Consultar resumen"}
              </Button>

              {canDownload && (
                <Button
                  variant="outlined"
                  startIcon={
                    isDownloading ? (
                      <CircularProgress size={18} color="inherit" aria-label="Generando reporte" />
                    ) : (
                      <CloudDownloadOutlinedIcon />
                    )
                  }
                  onClick={handleDownload}
                  disabled={isDownloading}
                  sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 190 } }}
                >
                  {isDownloading ? "Generando reporte..." : "Descargar Excel"}
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>

        {projectsError && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            action={
              <Button color="inherit" size="small" startIcon={<RefreshOutlinedIcon />} onClick={loadProjects}>
                Reintentar
              </Button>
            }
          >
            {projectsError}
          </Alert>
        )}

        {showHistoricalWarning && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            El reporte histórico consolidado puede tardar más tiempo en generarse.
          </Alert>
        )}

        {exportError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {exportError}
          </Alert>
        )}
      </Paper>

      <Box component="section" aria-labelledby="rse-summary-title">
        <Typography id="rse-summary-title" variant="h4" sx={{ fontWeight: 850, mb: 2 }}>
          Resumen
        </Typography>

        {summaryError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {summaryError}
          </Alert>
        )}

        {isLoadingSummary ? (
          <Paper
            sx={{
              ...surfaceSx,
              minHeight: 180,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Stack alignItems="center" spacing={1.5}>
              <CircularProgress aria-label="Cargando indicadores" />
              <Typography color="text.secondary">Consultando indicadores...</Typography>
            </Stack>
          </Paper>
        ) : summary ? (
          <Grid container spacing={2}>
            {KPI_ITEMS.map((item) => (
              <Grid item xs={12} sm={6} lg={4} key={item.key}>
                <Paper
                  sx={{
                    ...surfaceSx,
                    p: 2.25,
                    minHeight: 128,
                    borderTop: `4px solid ${item.color}`,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800, mb: 1 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 900 }}>
                    {formatRseNumber(summary[item.key])}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper
            sx={{
              ...surfaceSx,
              minHeight: 180,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Typography color="text.secondary">
              Selecciona los filtros y consulta el resumen para visualizar los indicadores.
            </Typography>
          </Paper>
        )}
      </Box>

      {analyticsRequested && (
        <Box component="section" aria-labelledby="rse-analytics-title" sx={{ mt: 4 }}>
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography id="rse-analytics-title" variant="h4" sx={{ fontWeight: 850 }}>
              Análisis del período
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Distribución demográfica, comunidades, patologías y calidad de los datos.
            </Typography>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <EdadSexoChart
                data={analyticsData.edadSexo}
                loading={
                  isLoadingAnalytics &&
                  analyticsData.edadSexo === null &&
                  !analyticsErrors.edadSexo
                }
                error={analyticsErrors.edadSexo}
              />
            </Grid>
            <Grid item xs={12} lg={5}>
              <TopComunidadesChart
                data={analyticsData.comunidades}
                showProjectCode={filters.projectId === null}
                loading={
                  isLoadingAnalytics &&
                  analyticsData.comunidades === null &&
                  !analyticsErrors.comunidades
                }
                error={analyticsErrors.comunidades}
              />
            </Grid>
            <Grid item xs={12} lg={7}>
              <TopPatologiasChart
                data={analyticsData.patologias}
                showProjectCode={filters.projectId === null}
                loading={
                  isLoadingAnalytics &&
                  analyticsData.patologias === null &&
                  !analyticsErrors.patologias
                }
                error={analyticsErrors.patologias}
              />
            </Grid>
            <Grid item xs={12}>
              <CalidadDatos
                data={analyticsData.calidad}
                loading={
                  isLoadingAnalytics &&
                  analyticsData.calidad === null &&
                  !analyticsErrors.calidad
                }
                error={analyticsErrors.calidad}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      <Box sx={{ mt: 4 }}>
        <PatologiasComunidad
          data={pathologyCommunityData}
          status={pathologyCommunityStatus}
          error={pathologyCommunityError}
          projectId={filters.projectId}
          projectLabel={selectedProjectLabel}
          periodLabel={selectedPeriodLabel}
          onLoad={handlePathologyCommunity}
        />
      </Box>

      <Snackbar
        open={downloadSuccess}
        autoHideDuration={4000}
        onClose={() => setDownloadSuccess(false)}
        message="Reporte generado correctamente."
      />
    </Container>
  );
};

export default ReporteSaludEmergencia;
